import { useState, useEffect, useCallback } from "react";

const ENVS = [
  { key: "tnpdcl", label: "⚡ TNPDCL", color: "#3b82f6" },
  { key: "fsa",    label: "🏭 FSA",    color: "#10b981" },
];

export default function UserPanel({ token, theme, currentUser }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [confirm, setConfirm] = useState(null); // {uid, username}
  const [msg,     setMsg]     = useState(null);

  const H = { "X-Auth-Token": token, "Content-Type": "application/json" };
  const BASE = "/auth";

  const flash = (m, ok=true) => {
    setMsg({text:m, ok});
    setTimeout(()=>setMsg(null), 3500);
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/users`, { headers: H });
      const d = await r.json();
      setUsers(d.users || []);
    } catch { setError("Failed to load users"); }
    setLoading(false);
  }, [token]);

  useEffect(()=>{ loadUsers(); }, [loadUsers]);

  const deleteUser = async (uid, username) => {
    const r = await fetch(`${BASE}/users/${uid}`, { method:"DELETE", headers: H });
    const d = await r.json();
    if (r.ok) { flash(`User '${username}' deleted`); loadUsers(); }
    else flash(d.error||"Delete failed", false);
    setConfirm(null);
  };

  const toggleActive = async (user) => {
    const r = await fetch(`${BASE}/users/${user.id}`, {
      method:"PUT", headers: H,
      body: JSON.stringify({ active: user.active ? 0 : 1 })
    });
    const d = await r.json();
    if (r.ok) { flash(d.message); loadUsers(); }
    else flash(d.error||"Update failed", false);
  };

  const card = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 14, padding: 24,
  };

  return (
    <div style={{ padding: "24px", maxWidth: 1000, margin: "0 auto", overflowY:"auto", height:"100%" }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:theme.text,
            fontFamily:"'Inter',sans-serif", letterSpacing:"-.02em" }}>
            👥 User Management
          </div>
          <div style={{ fontSize:13, color:theme.textMuted, marginTop:4,
            fontFamily:"'Inter',sans-serif" }}>
            Create and manage dashboard users and their access
          </div>
        </div>
        <button onClick={()=>{ setShowAdd(true); setEditUser(null); }} style={{
          background:"#3b82f6", border:"none", color:"#fff",
          borderRadius:8, padding:"10px 20px", cursor:"pointer",
          fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:700,
        }}>+ Add User</button>
      </div>

      {/* Flash message */}
      {msg && (
        <div style={{ marginBottom:16, padding:"12px 16px", borderRadius:8,
          background: msg.ok?"rgba(16,185,129,0.1)":"rgba(239,68,68,0.1)",
          border:`1px solid ${msg.ok?"#10b98140":"#ef444440"}`,
          color: msg.ok?"#10b981":"#ef4444",
          fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600 }}>
          {msg.ok?"✅":"❌"} {msg.text}
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAdd || editUser) && (
        <UserForm
          theme={theme} token={token} BASE={BASE} H={H}
          editUser={editUser}
          onDone={()=>{ setShowAdd(false); setEditUser(null); loadUsers(); }}
          onCancel={()=>{ setShowAdd(false); setEditUser(null); }}
          flash={flash}
        />
      )}

      {/* Users Table */}
      <div style={card}>
        <div style={{ fontSize:12, fontWeight:700, color:theme.textMuted,
          letterSpacing:".1em", marginBottom:16, fontFamily:"'Inter',sans-serif" }}>
          ALL USERS — {users.length} total
        </div>

        {loading ? (
          <div style={{ textAlign:"center", padding:40, color:theme.textMuted,
            fontFamily:"'Inter',sans-serif" }}>Loading...</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {/* Table Header */}
            <div style={{ display:"grid",
              gridTemplateColumns:"1fr 100px 160px 120px 120px 100px",
              gap:12, padding:"8px 12px",
              borderBottom:`1px solid ${theme.border}` }}>
              {["USERNAME","ROLE","ENVIRONMENTS","CREATED BY","CREATED","ACTIONS"].map(h=>(
                <div key={h} style={{ fontSize:11, fontWeight:700,
                  color:theme.textMuted, fontFamily:"'Inter',sans-serif",
                  letterSpacing:".08em" }}>{h}</div>
              ))}
            </div>

            {/* Rows */}
            {users.map(u => {
              const envs = u.envs ? JSON.parse(u.envs) : null;
              const isSystem = ["admin","devops"].includes(u.username);
              return (
                <div key={u.id} style={{
                  display:"grid",
                  gridTemplateColumns:"1fr 100px 160px 120px 120px 100px",
                  gap:12, padding:"12px",
                  borderRadius:8,
                  background: u.active ? "transparent" : "rgba(239,68,68,0.04)",
                  border:`1px solid ${u.active ? "transparent" : "rgba(239,68,68,0.15)"}`,
                  alignItems:"center",
                  transition:"background .15s",
                }}>
                  {/* Username */}
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{
                      width:32, height:32, borderRadius:"50%",
                      background: u.role==="admin"?"rgba(59,130,246,0.15)":"rgba(16,185,129,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:14, fontWeight:700,
                      color: u.role==="admin"?"#3b82f6":"#10b981",
                      fontFamily:"'Inter',sans-serif",
                    }}>{u.username[0].toUpperCase()}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:theme.text,
                        fontFamily:"'Inter',sans-serif" }}>{u.username}</div>
                      {!u.active && <div style={{ fontSize:11, color:"#ef4444",
                        fontFamily:"'Inter',sans-serif" }}>Disabled</div>}
                      {isSystem && <div style={{ fontSize:10, color:theme.textMuted,
                        fontFamily:"'Inter',sans-serif" }}>system</div>}
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    <span style={{
                      fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:5,
                      background: u.role==="admin"?"rgba(59,130,246,0.12)":"rgba(16,185,129,0.12)",
                      color: u.role==="admin"?"#3b82f6":"#10b981",
                      border:`1px solid ${u.role==="admin"?"#3b82f640":"#10b98140"}`,
                      fontFamily:"'Inter',sans-serif",
                    }}>{u.role==="admin"?"👑 Admin":"👁 Readonly"}</span>
                  </div>

                  {/* Envs */}
                  <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                    {envs ? envs.map(e=>(
                      <span key={e} style={{
                        fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:5,
                        background:"rgba(59,130,246,0.1)", color:"#3b82f6",
                        border:"1px solid #3b82f630",
                        fontFamily:"'Inter',sans-serif",
                      }}>{e.toUpperCase()}</span>
                    )) : (
                      <span style={{ fontSize:11, color:theme.textMuted,
                        fontFamily:"'Inter',sans-serif" }}>All Envs</span>
                    )}
                  </div>

                  {/* Created by */}
                  <div style={{ fontSize:12, color:theme.textMuted,
                    fontFamily:"'Inter',sans-serif" }}>{u.created_by}</div>

                  {/* Created at */}
                  <div style={{ fontSize:11, color:theme.textMuted,
                    fontFamily:"'JetBrains Mono',monospace" }}>
                    {new Date(u.created_at*1000).toLocaleDateString()}
                  </div>

                  {/* Actions */}
                  <div style={{ display:"flex", gap:6 }}>
                    {!isSystem && (
                      <>
                        <button onClick={()=>{ setEditUser(u); setShowAdd(false); }}
                          title="Edit" style={{
                            background:"rgba(59,130,246,0.1)",border:"1px solid #3b82f630",
                            color:"#3b82f6",borderRadius:6,padding:"4px 8px",
                            cursor:"pointer",fontSize:12,
                          }}>✏️</button>
                        <button onClick={()=>toggleActive(u)}
                          title={u.active?"Disable":"Enable"} style={{
                            background:u.active?"rgba(245,158,11,0.1)":"rgba(16,185,129,0.1)",
                            border:`1px solid ${u.active?"#f59e0b30":"#10b98130"}`,
                            color:u.active?"#f59e0b":"#10b981",
                            borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12,
                          }}>{u.active?"⏸":"▶"}</button>
                        <button onClick={()=>setConfirm({uid:u.id,username:u.username})}
                          title="Delete" style={{
                            background:"rgba(239,68,68,0.1)",border:"1px solid #ef444430",
                            color:"#ef4444",borderRadius:6,padding:"4px 8px",
                            cursor:"pointer",fontSize:12,
                          }}>🗑</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {confirm && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:999 }}>
          <div style={{ ...card, maxWidth:380, width:"90%" }}>
            <div style={{ fontSize:16, fontWeight:700, color:theme.text,
              fontFamily:"'Inter',sans-serif", marginBottom:8 }}>Delete User?</div>
            <div style={{ fontSize:13, color:theme.textMuted,
              fontFamily:"'Inter',sans-serif", marginBottom:20 }}>
              Are you sure you want to delete <strong style={{color:theme.text}}>{confirm.username}</strong>? This cannot be undone.
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={()=>setConfirm(null)} style={{
                background:theme.inputBg,border:`1px solid ${theme.border}`,
                color:theme.textMuted,borderRadius:6,padding:"8px 16px",
                cursor:"pointer",fontFamily:"'Inter',sans-serif",fontSize:13,
              }}>Cancel</button>
              <button onClick={()=>deleteUser(confirm.uid, confirm.username)} style={{
                background:"#ef4444",border:"none",color:"#fff",
                borderRadius:6,padding:"8px 16px",cursor:"pointer",
                fontFamily:"'Inter',sans-serif",fontSize:13,fontWeight:700,
              }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserForm({ theme, token, BASE, H, editUser, onDone, onCancel, flash }) {
  const [username, setUsername] = useState(editUser?.username||"");
  const [password, setPassword] = useState("");
  const [role,     setRole]     = useState(editUser?.role||"readonly");
  const [allEnvs,  setAllEnvs]  = useState(!editUser?.envs);
  const [selEnvs,  setSelEnvs]  = useState(()=>{
    if (!editUser?.envs) return [];
    try { return JSON.parse(editUser.envs); } catch { return []; }
  });
  const [saving, setSaving] = useState(false);

  const toggleEnv = (key) => {
    setSelEnvs(prev => prev.includes(key) ? prev.filter(e=>e!==key) : [...prev, key]);
  };

  const submit = async () => {
    if (!editUser && (!username || !password)) {
      flash("Username and password required", false); return;
    }
    if (!allEnvs && selEnvs.length===0) {
      flash("Select at least one environment", false); return;
    }
    setSaving(true);
    const body = {
      role,
      envs: allEnvs ? null : selEnvs,
      ...(username && !editUser ? { username } : {}),
      ...(password ? { password } : {}),
    };
    const url  = editUser ? `${BASE}/users/${editUser.id}` : `${BASE}/users`;
    const meth = editUser ? "PUT" : "POST";
    if (!editUser) body.username = username;

    const r = await fetch(url, { method:meth, headers:H, body:JSON.stringify(body) });
    const d = await r.json();
    setSaving(false);
    if (r.ok) { flash(d.message); onDone(); }
    else flash(d.error||"Failed", false);
  };

  const card = { background:theme.bgCard, border:`1px solid ${theme.border}`,
    borderRadius:14, padding:24, marginBottom:20 };
  const label = { fontSize:12, fontWeight:700, color:theme.textMuted,
    fontFamily:"'Inter',sans-serif", letterSpacing:".08em", marginBottom:6, display:"block" };
  const input = { background:theme.inputBg, border:`1px solid ${theme.border}`,
    color:theme.text, borderRadius:8, padding:"10px 14px",
    fontFamily:"'Inter',sans-serif", fontSize:13, outline:"none", width:"100%",
    boxSizing:"border-box" };

  return (
    <div style={card}>
      <div style={{ fontSize:15, fontWeight:700, color:theme.text,
        fontFamily:"'Inter',sans-serif", marginBottom:20 }}>
        {editUser ? `✏️ Edit User — ${editUser.username}` : "➕ New User"}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        <div>
          <label style={label}>USERNAME</label>
          <input style={{...input, opacity:editUser?0.5:1}}
            value={username} onChange={e=>setUsername(e.target.value)}
            disabled={!!editUser} placeholder="e.g. john-doe"/>
        </div>
        <div>
          <label style={label}>{editUser?"NEW PASSWORD (leave blank to keep)":"PASSWORD"}</label>
          <input style={input} type="password"
            value={password} onChange={e=>setPassword(e.target.value)}
            placeholder={editUser?"••••••••":"Min 6 characters"}/>
        </div>
      </div>

      {/* Role */}
      <div style={{ marginBottom:16 }}>
        <label style={label}>ROLE</label>
        <div style={{ display:"flex", gap:8 }}>
          {[["admin","👑 Admin","#3b82f6"],["readonly","👁 Read Only","#10b981"]].map(([k,l,c])=>(
            <button key={k} onClick={()=>setRole(k)} style={{
              background: role===k?`${c}18`:theme.inputBg,
              border:`2px solid ${role===k?c:theme.border}`,
              color: role===k?c:theme.textMuted,
              borderRadius:8, padding:"10px 20px", cursor:"pointer",
              fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:700,
              transition:"all .15s",
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Environment Access */}
      <div style={{ marginBottom:20 }}>
        <label style={label}>ENVIRONMENT ACCESS</label>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={()=>setAllEnvs(true)} style={{
            background: allEnvs?"rgba(168,139,250,0.15)":theme.inputBg,
            border:`2px solid ${allEnvs?"#a78bfa":theme.border}`,
            color: allEnvs?"#a78bfa":theme.textMuted,
            borderRadius:8, padding:"8px 16px", cursor:"pointer",
            fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600,
          }}>🌐 All Environments</button>
          {ENVS.map(e=>(
            <button key={e.key} onClick={()=>{ setAllEnvs(false); toggleEnv(e.key); }} style={{
              background: !allEnvs&&selEnvs.includes(e.key)?`${e.color}18`:theme.inputBg,
              border:`2px solid ${!allEnvs&&selEnvs.includes(e.key)?e.color:theme.border}`,
              color: !allEnvs&&selEnvs.includes(e.key)?e.color:theme.textMuted,
              borderRadius:8, padding:"8px 16px", cursor:"pointer",
              fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600,
            }}>{e.label}</button>
          ))}
        </div>
      </div>

      <div style={{ display:"flex", gap:8 }}>
        <button onClick={submit} disabled={saving} style={{
          background:"#3b82f6", border:"none", color:"#fff",
          borderRadius:8, padding:"10px 24px", cursor:"pointer",
          fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:700,
          opacity: saving?0.7:1,
        }}>{saving?"Saving...": editUser?"💾 Save Changes":"➕ Create User"}</button>
        <button onClick={onCancel} style={{
          background:theme.inputBg, border:`1px solid ${theme.border}`,
          color:theme.textMuted, borderRadius:8, padding:"10px 24px", cursor:"pointer",
          fontFamily:"'Inter',sans-serif", fontSize:13,
        }}>Cancel</button>
      </div>
    </div>
  );
}
